import { CalendarIcon, ChevronLeft, ChevronRight } from "@cynthai/icons";
import type { ReactNode } from "react";
import { useContext } from "react";
import { useLocale } from "react-aria";
import type { ButtonProps, PopoverProps } from "react-aria-components";
import {
  Button,
  CalendarCell,
  CalendarGrid,
  CalendarGridBody,
  CalendarGridHeader,
  CalendarHeaderCell,
  CalendarStateContext,
  DateSegment,
  Dialog,
  Group,
  Heading,
  I18nProvider,
  Popover,
  RangeCalendarStateContext,
} from "react-aria-components";
import type { DateSegment as DateSegmentType } from "react-stately";
import { tv } from "tailwind-variants";

import { formatCalendarMonth, formatWeekday } from "./date-picker-format";
import { getLocale } from "./paraglide/runtime";

// map the app locale to a region that yields european day-month-year ordering.
const DATE_LOCALES: Record<string, string> = {
  en: "en-GB",
  nl: "nl-NL",
};

export function CalendarI18nProvider({
  children,
}: Readonly<{ children: ReactNode }>) {
  return (
    <I18nProvider locale={DATE_LOCALES[getLocale()] ?? "en-GB"}>
      {children}
    </I18nProvider>
  );
}

export function CalendarFieldGroup({
  children,
}: Readonly<{ children: ReactNode }>) {
  return (
    <Group className="bg-secondary text-text-primary focus-within:border-primary relative flex h-9 items-center gap-2 rounded-md border border-transparent px-3 text-sm leading-5 transition-colors">
      {children}
    </Group>
  );
}

// react-aria puts aria-selected on the <td> but data-* states on the inner
// cell this styles, so every state goes through data attributes
const calendarCell = tv({
  base: "data-disabled:text-text-secondary/30 data-outside-month:text-text-primary/25 data-focus-visible:ring-primary/50 mx-auto my-1 flex size-10 cursor-pointer items-center justify-center rounded-sm text-sm tabular-nums transition-colors outline-none data-disabled:cursor-not-allowed data-focus-visible:ring-1",
  variants: {
    selection: {
      single:
        "data-hovered:bg-row-hover data-selected:bg-primary data-selected:text-text-button data-selected:data-hovered:bg-primary/80",
      range:
        "data-hovered:bg-row-hover data-selected:bg-primary/20 data-selection-end:bg-primary data-selection-end:text-text-button data-selection-start:bg-primary data-selection-start:text-text-button",
    },
  },
});

export function CalendarDateSegment({
  segment,
}: Readonly<{ segment: DateSegmentType }>) {
  return (
    <DateSegment
      segment={segment}
      className="text-text-primary focus:bg-primary/20 rounded-sm px-0.5 caret-transparent focus:outline-none"
    />
  );
}

export function CalendarTriggerButton() {
  return (
    <Button className="text-text-tertiary hover:text-text-primary flex cursor-pointer items-center bg-transparent outline-none">
      <CalendarIcon className="size-5" />
    </Button>
  );
}

/** the month header and day grid, shared by single and range calendars */
export function CalendarMonth({
  selection,
}: Readonly<{ selection: "single" | "range" }>) {
  const { locale } = useLocale();
  const singleState = useContext(CalendarStateContext);
  const rangeState = useContext(RangeCalendarStateContext);
  const visibleMonth = (singleState ?? rangeState)?.visibleRange.start;

  return (
    <>
      <header className="flex items-center justify-between">
        <Heading className="text-xl leading-8 font-bold">
          {visibleMonth && formatCalendarMonth(visibleMonth, locale)}
        </Heading>
        <div className="flex gap-2">
          <RoundButton slot="previous">
            <ChevronLeft className="size-5" />
          </RoundButton>
          <RoundButton slot="next">
            <ChevronRight className="size-5" />
          </RoundButton>
        </div>
      </header>
      <CalendarGrid weekdayStyle="short" className="w-full border-collapse">
        <CalendarGridHeader>
          {(day) => (
            <CalendarHeaderCell className="text-text-primary h-12 text-sm font-semibold">
              {formatWeekday(day)}
            </CalendarHeaderCell>
          )}
        </CalendarGridHeader>
        <CalendarGridBody>
          {(date) => (
            <CalendarCell date={date} className={calendarCell({ selection })} />
          )}
        </CalendarGridBody>
      </CalendarGrid>
    </>
  );
}

function RoundButton(props: Readonly<ButtonProps>) {
  return (
    <Button
      {...props}
      className="text-text-primary hover:bg-row-hover flex size-9 cursor-pointer items-center justify-center rounded-md border-0 bg-transparent transition-colors outline-none"
    />
  );
}

export function CalendarPopover(props: Readonly<PopoverProps>) {
  return (
    <Popover
      placement="bottom start"
      {...props}
      className={({ isEntering, isExiting }) =>
        `bg-secondary border-border-table/50 w-87 overflow-auto rounded-lg border shadow-lg ${
          isEntering
            ? "animate-in fade-in placement-bottom:slide-in-from-top-1 placement-top:slide-in-from-bottom-1 duration-200 ease-out"
            : ""
        } ${
          isExiting
            ? "animate-out fade-out placement-bottom:slide-out-to-top-1 placement-top:slide-out-to-bottom-1 duration-150 ease-in"
            : ""
        }`
      }
    />
  );
}

export function CalendarDialog({
  children,
}: Readonly<{ children: ReactNode }>) {
  return (
    <Dialog className="text-text-primary px-4 py-3 outline-none">
      {children}
    </Dialog>
  );
}
